import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, TrendingDown, PieChart, BarChart3, Target, 
  Wallet, Building2, Car, Gem, ArrowUpRight, ArrowDownRight,
  Lock, Crown, Sparkles, DollarSign, Activity, ChevronRight,
  LineChart, Shield, Zap, ArrowLeft
} from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortfolioInsightsDashboard = () => {
  const navigate = useNavigate();
  const { formatPrice: fp } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      
      const response = await axios.get(`${API}/investor/portfolio-insights`, {
        withCredentials: true
      });
      setInsights(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('premium_required');
      } else {
        setError('fetch_error');
        toast.error('Failed to load portfolio insights');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => fp(amount);

  const formatFullCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', { 
      style: 'currency', 
      currency: 'AED', 
      minimumFractionDigits: 0 
    }).format(amount);
  };

  // Premium Required Screen
  if (error === 'premium_required') {
    return (
      <div className="min-h-screen bg-obsidian text-text-primary flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg text-center p-12"
        >
          <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-gold" />
          </div>
          <h2 className="font-playfair text-3xl font-semibold mb-4">Premium Feature</h2>
          <p className="text-text-secondary mb-8">
            Portfolio Insights Dashboard is available exclusively for Premium and Elite members. 
            Upgrade to unlock wealth management features, ROI projections, and personalized investment insights.
          </p>
          <div className="flex gap-4 justify-center">
            <Button 
              onClick={() => navigate('/subscriptions')}
              className="bg-gold text-black hover:bg-gold-light px-8"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
            <Button 
              onClick={() => navigate('/investor')}
              variant="outline"
              className="border-white/20 hover:bg-white/5"
            >
              Back to Investor
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian text-text-primary flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading Portfolio Insights...</p>
        </div>
      </div>
    );
  }

  const { summary, asset_value_growth, diversification, lifestyle_score, roi_projections, top_assets, market_insights } = insights || {};

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <Nav showBack activePage="/investor" />

      <div className="max-w-[1440px] mx-auto px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="px-3 py-1 bg-gold/20 rounded-full text-gold text-xs font-semibold uppercase tracking-wider flex items-center gap-1">
              <Crown className="w-3 h-3" />
              {insights?.subscription_tier === 'elite' ? 'Elite' : 'Premium'} Feature
            </div>
          </div>
          <h1 className="font-playfair text-4xl font-semibold mb-2">Portfolio Insights</h1>
          <p className="text-text-secondary">Your wealth management dashboard with real-time analytics</p>
        </motion.div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-gold" />
              </div>
              <span className={`flex items-center text-sm ${summary?.ytd_growth_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {summary?.ytd_growth_percent >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {summary?.ytd_growth_percent}%
              </span>
            </div>
            <p className="text-text-secondary text-sm mb-1">Total Portfolio Value</p>
            <p className="text-2xl font-bold text-gold">{formatCurrency(summary?.total_portfolio_value || 0)}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
            <p className="text-text-secondary text-sm mb-1">Monthly Potential Income</p>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(summary?.monthly_potential_income || 0)}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-400" />
              </div>
            </div>
            <p className="text-text-secondary text-sm mb-1">Total Assets</p>
            <p className="text-2xl font-bold">{summary?.total_assets || 0}</p>
            <p className="text-xs text-text-secondary mt-1">
              {summary?.properties_count} Properties · {summary?.marketplace_count} Luxury Items
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <p className="text-text-secondary text-sm mb-1">Lifestyle Score</p>
            <p className="text-2xl font-bold text-blue-400">{lifestyle_score?.overall_score || 0}/100</p>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'growth', label: 'Asset Growth', icon: TrendingUp },
            { id: 'diversification', label: 'Diversification', icon: PieChart },
            { id: 'lifestyle', label: 'Lifestyle Score', icon: Sparkles },
            { id: 'projections', label: 'ROI Projections', icon: LineChart },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-gold text-black font-semibold' 
                  : 'bg-white/5 text-text-secondary hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Asset Value Growth Chart */}
            {(activeTab === 'overview' || activeTab === 'growth') && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="glass-card p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-playfair text-xl font-semibold flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-gold" />
                    Asset Value Growth
                  </h3>
                  <span className="text-xs text-text-secondary">Last 12 months</span>
                </div>
                
                {/* Simple Bar Chart */}
                <div className="h-64 flex items-end gap-2">
                  {asset_value_growth?.map((data, idx) => {
                    const maxValue = Math.max(...asset_value_growth.map(d => d.value));
                    const height = (data.value / maxValue) * 100;
                    return (
                      <div key={`insight-${idx}`} className="flex-1 flex flex-col items-center gap-2">
                        <div 
                          className="w-full bg-gradient-to-t from-gold/80 to-gold/40 rounded-t-sm transition-all hover:from-gold hover:to-gold/60 cursor-pointer relative group"
                          style={{ height: `${height}%`, minHeight: '20px' }}
                        >
                          <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-black/90 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                            {formatCurrency(data.value)}
                          </div>
                        </div>
                        <span className="text-xs text-text-secondary">{data.month}</span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Diversification */}
            {(activeTab === 'overview' || activeTab === 'diversification') && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="glass-card p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-playfair text-xl font-semibold flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-gold" />
                    Investment Diversification
                  </h3>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  {/* Real Estate */}
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-text-secondary">Real Estate</p>
                        <p className="text-lg font-bold">{diversification?.overview?.real_estate?.percent || 0}%</p>
                      </div>
                    </div>
                    <p className="text-gold font-semibold">{formatCurrency(diversification?.overview?.real_estate?.value || 0)}</p>
                    <p className="text-xs text-text-secondary">{diversification?.overview?.real_estate?.count || 0} properties</p>
                  </div>
                  
                  {/* Luxury Assets */}
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center">
                        <Gem className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <p className="text-sm text-text-secondary">Luxury Assets</p>
                        <p className="text-lg font-bold">{diversification?.overview?.luxury_assets?.percent || 0}%</p>
                      </div>
                    </div>
                    <p className="text-gold font-semibold">{formatCurrency(diversification?.overview?.luxury_assets?.value || 0)}</p>
                    <p className="text-xs text-text-secondary">{diversification?.overview?.luxury_assets?.count || 0} items</p>
                  </div>
                </div>

                {/* Property Types Breakdown */}
                {Object.keys(diversification?.property_types || {}).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <p className="text-sm text-text-secondary mb-3">Property Types</p>
                    <div className="space-y-2">
                      {Object.entries(diversification?.property_types || {}).map(([type, data]) => (
                        <div key={type} className="flex items-center justify-between">
                          <span className="text-sm">{type}</span>
                          <span className="text-gold font-semibold">{formatCurrency(data.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Lifestyle Score Details */}
            {(activeTab === 'lifestyle') && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="glass-card p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-playfair text-xl font-semibold flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-gold" />
                    ASTIIV BASGAT Lifestyle Score
                  </h3>
                  <div className="text-3xl font-bold text-gold">{lifestyle_score?.overall_score}/100</div>
                </div>
                
                <div className="space-y-6">
                  {[
                    { key: 'wealth_alignment', label: 'Wealth Alignment', icon: Wallet, color: 'gold', desc: 'Portfolio value vs. stated budget' },
                    { key: 'lifestyle_match', label: 'Lifestyle Match', icon: Target, color: 'purple-400', desc: 'Profile completeness & preferences' },
                    { key: 'investment_readiness', label: 'Investment Readiness', icon: Zap, color: 'emerald-400', desc: 'Subscription tier & activity level' },
                    { key: 'diversification_health', label: 'Diversification Health', icon: Shield, color: 'blue-400', desc: 'Asset variety & spread' },
                  ].map((metric) => (
                    <div key={metric.key}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <metric.icon className={`w-4 h-4 text-${metric.color}`} />
                          <span className="text-sm font-medium">{metric.label}</span>
                        </div>
                        <span className="text-sm font-bold">{lifestyle_score?.[metric.key] || 0}/100</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-${metric.color} rounded-full transition-all duration-1000`}
                          style={{ width: `${lifestyle_score?.[metric.key] || 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-text-secondary mt-1">{metric.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* ROI Projections */}
            {(activeTab === 'overview' || activeTab === 'projections') && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="glass-card p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-playfair text-xl font-semibold flex items-center gap-2">
                    <LineChart className="w-5 h-5 text-gold" />
                    5-Year ROI Projections
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 text-text-secondary text-sm font-normal">Year</th>
                        <th className="text-right py-3 text-text-secondary text-sm font-normal">Portfolio Value</th>
                        <th className="text-right py-3 text-text-secondary text-sm font-normal">Rental Income</th>
                        <th className="text-right py-3 text-text-secondary text-sm font-normal">Capital Gain</th>
                        <th className="text-right py-3 text-text-secondary text-sm font-normal">ROI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roi_projections?.map((proj) => (
                        <tr key={proj.year} className="border-b border-white/5">
                          <td className="py-4 font-semibold">Year {proj.year}</td>
                          <td className="py-4 text-right text-gold">{formatCurrency(proj.projected_value)}</td>
                          <td className="py-4 text-right text-emerald-400">+{formatCurrency(proj.rental_income)}</td>
                          <td className="py-4 text-right text-purple-400">+{formatCurrency(proj.capital_gain)}</td>
                          <td className="py-4 text-right">
                            <span className="bg-gold/20 text-gold px-2 py-1 rounded text-sm font-bold">
                              {proj.roi_percent}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <p className="text-xs text-text-secondary mt-4">
                  * Projections based on 6% annual appreciation and 5% rental yield. Actual returns may vary.
                </p>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Top Performing Assets */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-6"
            >
              <h3 className="font-playfair text-lg font-semibold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-gold" />
                Top Performing Assets
              </h3>
              
              {top_assets?.length > 0 ? (
                <div className="space-y-4">
                  {top_assets.map((asset, idx) => (
                    <Link 
                      key={asset.id}
                      to={asset.type === 'property' ? `/properties/${asset.id}` : `/marketplace/${asset.id}`}
                      className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <span className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center text-gold text-xs font-bold">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{asset.title}</p>
                        <p className="text-xs text-text-secondary">{asset.category}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gold">{asset.score}</p>
                        <p className="text-xs text-text-secondary">Score</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-text-secondary text-sm mb-4">No assets in portfolio yet</p>
                  <Button 
                    onClick={() => navigate('/properties')}
                    size="sm"
                    className="bg-gold text-black hover:bg-gold-light"
                  >
                    Browse Properties
                  </Button>
                </div>
              )}
            </motion.div>

            {/* Market Insights */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6"
            >
              <h3 className="font-playfair text-lg font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-gold" />
                Market Insights
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">Dubai Market</span>
                  <span className={`text-sm font-semibold flex items-center gap-1 ${
                    market_insights?.dubai_market_trend === 'bullish' ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {market_insights?.dubai_market_trend === 'bullish' ? (
                      <><TrendingUp className="w-4 h-4" /> Bullish</>
                    ) : (
                      <><TrendingDown className="w-4 h-4" /> Bearish</>
                    )}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">Luxury Segment Growth</span>
                  <span className="text-sm font-semibold text-emerald-400">+{market_insights?.luxury_segment_growth}%</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">Rental Demand</span>
                  <span className="text-sm font-semibold capitalize">{market_insights?.rental_demand}</span>
                </div>
                
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-text-secondary mb-2">Top Performing Areas</p>
                  <div className="flex flex-wrap gap-2">
                    {market_insights?.best_performing_areas?.map((area, idx) => (
                      <span key={`tag-${idx}`} className="text-xs bg-gold/20 text-gold px-2 py-1 rounded">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-text-secondary mb-2">Recommended Action</p>
                  <p className="text-sm font-semibold text-gold flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    {market_insights?.recommended_action}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6"
            >
              <h3 className="font-playfair text-lg font-semibold mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/investor')}
                  className="w-full bg-gold/20 text-gold hover:bg-gold/30 justify-start"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  ROI Calculator
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Button>
                <Button 
                  onClick={() => navigate('/properties')}
                  className="w-full bg-white/5 hover:bg-white/10 justify-start"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Browse Properties
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Button>
                <Button 
                  onClick={() => navigate('/wishlist')}
                  className="w-full bg-white/5 hover:bg-white/10 justify-start"
                >
                  <Gem className="w-4 h-4 mr-2" />
                  Manage Wishlist
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioInsightsDashboard;
